// Renewal.js
import React, { useEffect, useState, useCallback } from 'react';
import NavBar from '../NavBar.js';
import styles from '../styles/MediosPago.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

// Firebase
import { auth, db } from '../../firebaseConfig';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where,
  updateDoc
} from 'firebase/firestore';

// Imágenes
import redebanLogo from '../../assets/redBank.png';
import breb from '../../assets/bre-b.png';
import redebanQR from '../../assets/redebanQR.png';
import binanceLogo from '../../assets/binance.png';
import binanceQR from '../../assets/binanceQR.png';
import aviso from '../../assets/aviso.jpg';

const CLOUD_FN_BASE_URL = process.env.REACT_APP_VERIFY_FN_URL;
if (!CLOUD_FN_BASE_URL) {
  console.error('REACT_APP_VERIFY_FN_URL no está configurada');
}

const CREDENTIALS_COVER_IMG = aviso;
const TERMS_CONDITIONS_URL = "https://drive.google.com/file/d/1aESo-IebajZ8BaTIrkh4LPeCuOd7NuU8/view?usp=drive_link";

// ---------- UTILIDADES ----------
async function guardarPaycheckUnico(pagoData) {
  const user = auth.currentUser;
  const idmensaje =
    pagoData?.mensajeId ??
    `${pagoData?.fecha || 'sinf'}_${pagoData?.hora || 'sinh'}_${(pagoData?.nombreCompleto || pagoData?.usuario || 'sinN').replace(/\s+/g, '_')}_${pagoData?.monto || 'sinM'}`;

  const payload = {
    uid: user?.uid ?? null,
    fecha: pagoData?.fecha ?? null,
    hora: pagoData?.hora ?? null,
    flag: false,
    monto: pagoData?.monto ?? null,
    nombre: pagoData?.nombreCompleto ?? pagoData?.usuario ?? null,
    idmensaje: idmensaje,
    createdAt: serverTimestamp(),
  };

  const ref = doc(db, 'Paycheck', idmensaje);
  const snap = await getDoc(ref);
  if (snap.exists()) return { already: true, id: ref.id };

  await setDoc(ref, payload);
  return { already: false, id: ref.id };
}

async function obtenerDatosUsuario(uid) {
  try {
    const userDoc = await getDoc(doc(db, 'Usuarios', uid));
    if (userDoc.exists()) {
      const d = userDoc.data();
      return { email: d.email || null, phoneNumber: d.phoneNumber || null, nombre: d.displayName || null };
    }
    return { email: null, phoneNumber: null, nombre: null };
  } catch (err) {
    console.error('Error obteniendo datos usuario:', err);
    return { email: null, phoneNumber: null, nombre: null };
  }
}

async function obtenerRolUsuario(uid) {
  try {
    const userDoc = await getDoc(doc(db, 'Usuarios', uid));
    if (!userDoc.exists()) return null;
    const raw = userDoc.data().rol;
    return typeof raw === 'string' ? raw.trim() : null;
  } catch (error) {
    console.error('Error obteniendo rol del usuario:', error);
    return null;
  }
}

async function obtenerDuracionProducto(plataforma, uid) {
  try {
    const rolUsuario = await obtenerRolUsuario(uid);
    const rol = (rolUsuario || '').toLowerCase().trim();
    const esUsuarioFinal = ['usuarios', 'usuario', 'user', 'cliente'].includes(rol);
    const primario = esUsuarioFinal ? 'catalogo' : 'catalogoVendedores';
    const secundario = esUsuarioFinal ? 'catalogoVendedores' : 'catalogo';
    const plataformaNorm = String(plataforma || '').trim();

    let snap = await getDocs(query(collection(db, primario), where('plataforma', '==', plataformaNorm)));
    if (snap.empty) {
      snap = await getDocs(query(collection(db, secundario), where('plataforma', '==', plataformaNorm)));
    }
    if (!snap.empty) {
      const producto = snap.docs[0].data();
      return producto.duracion || null;
    }
    return null;
  } catch (error) {
    console.error('Error obteniendo duración del producto:', error);
    return null;
  }
}

function extraerDiasDeDuracion(duracion) {
  if (!duracion) return 30;
  const match = duracion.match(/(\d+)\s*d[ií]as?/i);
  return match ? parseInt(match[1], 10) : 30;
}

function normalizarAnioYY(y) {
  if (y >= 0 && y <= 99) return y <= 69 ? (2000 + y) : (1900 + y);
  return y;
}

function parseYYYYMMDD(s) {
  const t = String(s).trim().replace(/-/g, '/');
  const m = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(t);
  if (!m) return null;
  const y = parseInt(m[1], 10), M = parseInt(m[2], 10), d = parseInt(m[3], 10);
  const dt = new Date(Date.UTC(y, M-1, d)); 
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function parseDDMMYY(s) {
  const t = String(s).trim().replace(/-/g, '/');
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/.exec(t);
  if (!m) return null;
  const d = parseInt(m[1],10), M = parseInt(m[2],10); 
  let y = normalizarAnioYY(parseInt(m[3],10));
  const dt = new Date(Date.UTC(y, M-1, d)); 
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function parseFlexible(s) {
  const t = String(s||'').trim().replace(/-/g, '/');
  const parts = t.split('/');
  if (parts.length !== 3) return null;
  let [a,b,c] = parts.map(v => parseInt(v,10));
  if ([a,b,c].some(n => Number.isNaN(n))) return null;
  let y,M,d;
  if (a > 31) { y=a; M=b; d=c; } else { d=a; M=b; y=c; }
  if (c <= 99) y = normalizarAnioYY(y);
  const dt = new Date(Date.UTC(y, M-1, d)); 
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function parsearFechaPorMedio(fechaString, medioPago) {
  if (!fechaString) return null;
  const s = String(fechaString).trim().replace(/-/g, '/');
  if (medioPago === 'Redeban') return parseYYYYMMDD(s) || parseFlexible(s);
  if (medioPago === 'Bre-B') return parseDDMMYY(s) || parseFlexible(s);
  return parseFlexible(s);
}

function sumarDiasAFecha(fechaBaseStr, diasAgregar) {
  try {
    let start = null;
    if (fechaBaseStr) {
      start = parseFlexible(String(fechaBaseStr).replace(/-/g,'/')) || parsearFechaPorMedio(fechaBaseStr, 'Redeban') || null;
    }
    if (!start) start = new Date();
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + (diasAgregar || 0));
    const dd = String(end.getUTCDate()).padStart(2,'0');
    const mm = String(end.getUTCMonth()+1).padStart(2,'0');
    const yyyy = end.getUTCFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch (e) {
    console.error('Error sumando días:', e);
    return fechaBaseStr || '';
  }
}


async function aplicarRenovacionEnVenta(
  cuentaDocId,
  pagoData,
  medioPago,
  nombreComprador,
  diasAAgregar,
  plataforma,
  state,
  duracionTexto
) {
  try {
    const codigoCuenta = state?.codigo || null;
    const tipoCuenta = (state?.tipoCuenta || '').toLowerCase(); // <- normalizamos

    // ✅ Verifica si es cuenta completa
    const esCuentaCompleta =
      tipoCuenta.includes('completa') || tipoCuenta.includes('completo');

    // Utilidad para sumar días
    const calcUpdate = (existing) => {
      const baseFecha =
        existing?.fechaCorte || existing?.fechaInicio || pagoData.fecha || null;
      const nuevaFechaCorte = sumarDiasAFecha(baseFecha, diasAAgregar);
      const existingDiasDuracion = Number(existing?.diasDuracion || 0);
      const nuevoDiasDuracion = existingDiasDuracion + diasAAgregar;
      return { nuevaFechaCorte, nuevoDiasDuracion };
    };

    // ✅ CASO 1: CUENTA COMPLETA — actualiza TODOS los docs con mismo código
    if (esCuentaCompleta && codigoCuenta) {
      const q = query(
        collection(db, 'ventasOnline'),
        where('uid', '==', auth.currentUser.uid),
        where('codigo', '==', codigoCuenta)
      );
      const snap = await getDocs(q);

      if (snap.empty) {
        // Si no hay registros previos, crea uno nuevo
        const nuevaFechaInicio = pagoData.fecha;
        const nuevaFechaCorte = sumarDiasAFecha(nuevaFechaInicio, diasAAgregar);
        const ventaRef = doc(db, 'ventasOnline', cuentaDocId);

        await setDoc(ventaRef, {
          codigo: codigoCuenta,
          fechaInicio: nuevaFechaInicio,
          fechaCorte: nuevaFechaCorte,
          plataforma,
          uid: auth.currentUser.uid,
          correo: (await obtenerDatosUsuario(auth.currentUser.uid)).email,
          numero: (await obtenerDatosUsuario(auth.currentUser.uid)).phoneNumber,
          idTransaccion: pagoData.mensajeId || pagoData.idmensaje,
          medioPago,
          hora: pagoData.hora,
          nombreComprador,
          diasDuracion: diasAAgregar,
          duracionOriginal: duracionTexto || `${diasAAgregar} días`,
          tipoCuenta: tipoCuenta || 'cuenta completa',
          createdAt: serverTimestamp(),
          activo: true,
          estado: 'entregado',
        });

        return { success: true, created: true };
      }

      // Actualizamos todos los documentos con ese código
      const actualizados = [];
      for (const d of snap.docs) {
        const existing = d.data();
        const { nuevaFechaCorte, nuevoDiasDuracion } = calcUpdate(existing);

        await updateDoc(d.ref, {
          fechaCorte: nuevaFechaCorte,
          diasDuracion: nuevoDiasDuracion,
          medioPago,
          idTransaccion: pagoData.mensajeId || pagoData.idmensaje,
          nombreComprador,
          updatedAt: serverTimestamp(),
          activo: true,
        });

        actualizados.push({ id: d.id, nuevaFechaCorte, nuevoDiasDuracion });
      }

      return {
        success: true,
        updatedMany: true,
        count: actualizados.length,
        detalles: actualizados,
      };
    }

    // ✅ CASO 2: NO es cuenta completa — solo actualiza el documento actual
    const ventaRef = doc(db, 'ventasOnline', cuentaDocId);
    const ventaSnap = await getDoc(ventaRef);

    if (!ventaSnap.exists()) {
      // Si el doc no existe aún, creamos uno nuevo
      const nuevaFechaInicio = pagoData.fecha;
      const nuevaFechaCorte = sumarDiasAFecha(nuevaFechaInicio, diasAAgregar);

      await setDoc(ventaRef, {
        codigo: codigoCuenta || 'N/A',
        fechaInicio: nuevaFechaInicio,
        fechaCorte: nuevaFechaCorte,
        plataforma,
        uid: auth.currentUser.uid,
        correo: (await obtenerDatosUsuario(auth.currentUser.uid)).email,
        numero: (await obtenerDatosUsuario(auth.currentUser.uid)).phoneNumber,
        idTransaccion: pagoData.mensajeId || pagoData.idmensaje,
        medioPago,
        hora: pagoData.hora,
        nombreComprador,
        diasDuracion: diasAAgregar,
        duracionOriginal: duracionTexto || `${diasAAgregar} días`,
        tipoCuenta: tipoCuenta || '1 pantalla',
        createdAt: serverTimestamp(),
        activo: true,
        estado: 'entregado',
      });

      return { success: true, created: true };
    } else {
      // Actualiza solo ese documento
      const existing = ventaSnap.data();
      const { nuevaFechaCorte, nuevoDiasDuracion } = calcUpdate(existing);

      await updateDoc(ventaRef, {
        fechaCorte: nuevaFechaCorte,
        diasDuracion: nuevoDiasDuracion,
        medioPago,
        idTransaccion: pagoData.mensajeId || pagoData.idmensaje,
        nombreComprador,
        updatedAt: serverTimestamp(),
        activo: true,
      });

      return {
        success: true,
        updated: true,
        nuevaFechaCorte,
        nuevoDiasDuracion,
      };
    }
  } catch (err) {
    console.error('Error aplicando renovación en ventasOnline:', err);
    return { success: false, error: err.message };
  }
}



async function guardarReciboSimple(pagoData, medioPago, totalCompra, plataforma, state, duracionTexto, buyerName, datosUsuario) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Usuario no autenticado');

    const reciboId = pagoData.mensajeId || pagoData.idmensaje || `recibo_${Date.now()}`;
    const reciboRef = doc(db, 'receipts', reciboId);
    const reciboPayload = {
      medioPago,
      fecha: pagoData.fecha,
      hora: pagoData.hora,
      idmensaje: pagoData.mensajeId || pagoData.idmensaje,
      uid: user.uid,
      correo: datosUsuario.email,
      telefono: datosUsuario.phoneNumber,
      nombreComprador: pagoData.nombreCompleto || pagoData.usuario || buyerName || datosUsuario.nombre,
      valorCompra: totalCompra,
      valorTransferido: pagoData.monto || null,
      productosComprados: [{ plataforma, codigo: state.codigo || null, duracion: duracionTexto }],
      createdAt: serverTimestamp(),
      procesado: true,
      estado: 'renovacion'
    };
    const snap = await getDoc(reciboRef);
    if (!snap.exists()) await setDoc(reciboRef, reciboPayload);
    return { success: true, id: reciboId };
  } catch (err) {
    console.error('Error guardando recibo simple:', err);
    return { success: false, error: err.message };
  }
}

// ---------- COMPONENTE Renewal ----------
const Renewal = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state || {};
  const { cuentaId, plataforma, precio = null, moneda = 'COP' } = state;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState(null);
  const [duracionTexto, setDuracionTexto] = useState(null);
  const [diasAgregar, setDiasAgregar] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      if (!plataforma || !auth.currentUser) return;
      const dur = await obtenerDuracionProducto(plataforma, auth.currentUser.uid);
      setDuracionTexto(dur);
      setDiasAgregar(extraerDiasDeDuracion(dur));
    })();
  }, [plataforma]);

  const openBrebModal = () => { setModalType('breb'); setModalOpen(true); };
  const openRedebanModal = () => { setModalType('redeban'); setModalOpen(true); };
  const openBinanceModal = () => { setModalType('binance'); setModalOpen(true); };
  const openTermsConditions = () => { window.open(TERMS_CONDITIONS_URL, '_blank', 'noopener,noreferrer'); };

  const closeModal = useCallback(() => {
    if (showSuccess) {
      navigate('/activate-accounts');
    }
    setModalOpen(false);
    setVerifyMsg(null);
    setVerifying(false);
    setBuyerName('');
  }, [showSuccess, navigate]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && closeModal();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeModal]);

  const verificarPago = async () => {
    setVerifyMsg(null);
    if (!buyerName || buyerName.trim().length < 3) {
      setVerifyMsg({ ok: false, text: 'Ingresa el nombre del titular de la cuenta bancaria.' });
      return;
    }
    if (!precio || Number(precio) <= 0) {
      setVerifyMsg({ ok: false, text: 'El total a verificar es inválido (0).' });
      return;
    }

    setVerifying(true);
    try {
      const baseUrl = (CLOUD_FN_BASE_URL || '').replace(/\/$/, '');
      let endpointUrl, medioPago, bodyData;

      switch(modalType) {
        case 'binance':
          endpointUrl = `${baseUrl}/binance`;
          medioPago = 'Binance';
          bodyData = { usuario: buyerName.trim(), monto: Number(precio) };
          break;
        case 'breb':
          endpointUrl = `${baseUrl}/breb`;
          medioPago = 'Bre-B';
          bodyData = { nombre: buyerName.trim(), monto: Number(precio) };
          break;
        default:
          endpointUrl = `${baseUrl}/bancolombia`;
          medioPago = 'Redeban';
          bodyData = { nombre: buyerName.trim(), monto: Number(precio) };
          break;
      }

      console.log('🌐 Endpoint final:', endpointUrl);
      console.log('📝 Datos a enviar:', bodyData);

      const res = await fetch(endpointUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData)
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ Error HTTP:', res.status, res.statusText, errorText);
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      console.log('✅ Respuesta del servidor:', data);

      if (data?.success) {
        const pagoData = data.data;
        const datosUsuario = await obtenerDatosUsuario(auth.currentUser.uid);

        try {
          const resSave = await guardarPaycheckUnico(pagoData);

          if (resSave.already) {
            const paycheckRef = doc(db, 'Paycheck', pagoData.mensajeId);
            const paycheckDoc = await getDoc(paycheckRef);
            const paycheckExistente = paycheckDoc.data();

            if (paycheckExistente.flag === true) {
              setVerifyMsg({
                ok: false,
                text: `Esta compra ya fue entregada anteriormente con el código ${pagoData.mensajeId}. La renovación ya fue procesada.`
              });
              return;
            }
          }

          const nombreComprador = pagoData.nombreCompleto || pagoData.usuario;
          let diasAAgregar = diasAgregar;
          if (!diasAAgregar) {
            const dur = await obtenerDuracionProducto(plataforma, auth.currentUser.uid);
            diasAAgregar = extraerDiasDeDuracion(dur);
          }

          const resultado = await aplicarRenovacionEnVenta(
            cuentaId, 
            pagoData, 
            medioPago, 
            nombreComprador, 
            diasAAgregar,
            plataforma,
            state,
            duracionTexto
          );

          if (resultado.success) {
            await guardarReciboSimple(
              pagoData, 
              medioPago, 
              precio,
              plataforma,
              state,
              duracionTexto,
              buyerName,
              datosUsuario
            );

            try {
              const pcRef = doc(db, 'Paycheck', pagoData.mensajeId);
              const pcSnap = await getDoc(pcRef);
              if (pcSnap.exists()) {
                await updateDoc(pcRef, { flag: true });
              }
            } catch (e) {
              console.warn('No fue posible marcar Paycheck flag:', e);
            }

            setVerifyMsg({
              ok: true,
              text: `✅ Pago ${medioPago} verificado y renovación aplicada\nFecha: ${pagoData.fecha} • Hora: ${pagoData.hora} • ID: ${pagoData.mensajeId}`
            });
            setShowSuccess(true);
          } else {
            setVerifyMsg({
              ok: false,
              text: resultado.message || 'Error aplicando renovación'
            });
          }

        } catch (err) {
          console.error('❌ Error en el proceso:', err);
          setVerifyMsg({
            ok: false,
            text: '❌ Error procesando la renovación. Contacta soporte.'
          });
        }
      } else {
        setVerifyMsg({
          ok: false,
          text: data?.message || `❌ No se encontró pago ${medioPago} coincidente.`,
        });
      }
    } catch (err) {
      console.error('❌ Error completo al conectar:', err);
      setVerifyMsg({ ok: false, text: `❌ Error al conectar con el servidor: ${err.message}` });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <NavBar />
      <div className={styles.wrapper}>
        <h1 className={styles.title}>Renovar Servicio</h1>

        <div className={styles.buttonsContainer}>
          <div className={styles.payCard}>
            <button
              className={styles.payButton}
              onClick={openRedebanModal}
              title="Pagar con Redeban (mostrar QR)"
            >
              <img src={redebanLogo} alt="Redeban" className={styles.payIcon} />
            </button>
            <p className={styles.payText}>Redeban (Colombia)</p>
          </div>

          <div className={styles.payCard}>
            <button
              className={styles.payButton}
              onClick={openBrebModal}
              title="Pagar con llave"
            >
              <img src={breb} alt="Bre-B" className={styles.payIcon} />
            </button>
            <p className={styles.payText}>Paga con llave (Colombia)</p>
          </div>

          <div className={styles.payCard}>
            <button
              className={styles.payButton}
              onClick={openBinanceModal}
              title="Pagar con Binance"
            >
              <img src={binanceLogo} alt="Binance" className={styles.payIcon} />
            </button>
            <p className={styles.payText}>Binance Pay (USDT)</p>
          </div>
        </div>

        {/* Información de la renovación */}
        <div className={styles.credencialesBox} style={{ marginTop: '2rem' }}>
          <h3>📋 Detalles de la renovación</h3>
          <div className={styles.credencialesInfo}>
            <div className={styles.credencialItem}>
              <strong>Plataforma:</strong> {plataforma}
            </div>
            <div className={styles.credencialItem}>
              <strong>Precio:</strong> ${precio ? Number(precio).toLocaleString('es-CO') : '—'}
            </div>
            <div className={styles.credencialItem}>
              <strong>Duración:</strong> {duracionTexto || 'Cargando...'}
            </div>
          </div>
        </div>

        {/* Modal Unificado */}
        {modalOpen && (
          <>
            <div className={styles.modalOverlay} onClick={closeModal} />
            <div
              className={styles.modal}
              role="dialog"
              aria-modal="true"
              aria-label="Modal de pago"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>
                  {modalType === 'breb' ? 'Llave de pago Bre-B' :
                   modalType === 'binance' ? 'Escanea tu QR de Binance' :
                   'Escanea tu QR de Redeban'}
                </h2>
                <button
                  className={styles.modalClose}
                  onClick={closeModal}
                  aria-label="Cerrar"
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>

              <p className={styles.modalSubtitle}>
                Valor: <strong>${precio ? Number(precio).toLocaleString('es-CO') : '0'}</strong>
              </p>

              {modalType === 'binance' ? (
                <>
                  <div className={styles.instructions}>
                    <ol>
                      <div className={styles.example}>
                        1. Abre tu app de Binance y escanea el QR de pago.
                        <br />2. Envía el valor exacto: <strong>${precio ? Number(precio).toLocaleString('es-CO') : '0'} USDT</strong>
                        <br />3. Completa el pago.
                        <br />4. <strong>IMPORTANTE: </strong> Ingresa tu usuario de Binance tal como estas registrad@ en la app.
                        <br />5. Presiona <strong>Verificar pago</strong> para procesar tu renovación al instante.
                      </div>
                    </ol>
                  </div>

                  <div className={styles.qrWhiteBox} style={{ display: 'grid', placeItems: 'center' }}>
                    <img
                      src={binanceQR}
                      alt="Código QR Binance para pago"
                      className={styles.qrImage}
                    />
                  </div>
                </>
              ) : modalType === 'breb' ? (
                <>
                  <div className={styles.keyBox}>
                    <p><strong>Llave:</strong> 3188875678</p>
                    <button
                      className={styles.copyBtn}
                      onClick={() => navigator.clipboard.writeText('3188875678')}
                      title="Copiar llave"
                    >
                      📋 Copiar llave
                    </button>
                  </div>

                  <div className={styles.instructions}>
                    <ol>
                      <div className={styles.example}>
                        1. Abre tu app bancaria.
                        <br />2. Selecciona Tranferir con llaves (Bre-B).
                        <br />3. Ingresa la llave: <strong>3188875678</strong>
                        <br />4. Transfiere el valor exacto: <strong>${precio ? Number(precio).toLocaleString('es-CO') : '0'}</strong>
                        <br />5. Completada la transferencia, ingresa el nombre del titular de la cuanta y presiona <strong>Verificar pago</strong>.
                        <br />Si el pago es correcto, procesaremos tu renovación al instante.
                      </div>
                    </ol>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.instructions}>
                    <ol>
                      <div className={styles.example}>
                        1. Abre tu app bancaria y escanea el QR de pago.
                        <br />2. Transfiere el valor exacto: <strong>${precio ? Number(precio).toLocaleString('es-CO') : '0'}</strong>
                        <br />3. Ingresa el nombre del titular de la cuenta.
                        <br />4. Completado el pago, presiona <strong>Verificar pago</strong> abajo.
                        <br />Si el pago es correcto, procesaremos tu renovación al instante.
                      </div>
                    </ol>
                  </div>

                  <div className={styles.qrWhiteBox} style={{ display: 'grid', placeItems: 'center' }}>
                    <img
                      src={redebanQR}
                      alt="Código QR Redeban para pago"
                      className={styles.qrImage}
                    />
                  </div>
                </>
              )}

              <div className={styles.formGroup} style={{ marginTop: '1rem' }}>
                <label className={styles.formLabel}>
                  {modalType === 'binance'
                    ? 'Escribe tu nombre de usuario de Binance:'
                    : 'Escribe el nombre del titular de la cuenta bancaria:'
                  }
                </label>
                <input
                  className={styles.input}
                  type="text"
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  placeholder={modalType === 'binance' ? "Tu usuario de Binance" : "Ej: JUAN JOSE PEREZ GOMEZ"}
                />
              </div>

              <div className={styles.actionsRow} style={{ justifyContent: 'center', marginTop: '.75rem' }}>
                {modalType === 'redeban' && (
                  <a
                    href={redebanQR}
                    download="QR-Redeban.png"
                    className={styles.secondaryBtn}
                    title="Descargar imagen del QR"
                  >
                    ⬇️ Descargar QR
                  </a>
                )}

                {modalType === 'binance' && (
                  <a
                    href={binanceQR}
                    download="QR-Binance.png"
                    className={styles.secondaryBtn}
                    title="Descargar imagen del QR"
                  >
                    ⬇️ Descargar QR
                  </a>
                )}

                <button
                  className={styles.primaryBtn}
                  onClick={verificarPago}
                  disabled={verifying || !buyerName || !precio}
                  title={!buyerName ? 'Ingresa el nombre' : undefined}
                >
                  {verifying ? '⏳ Verificando…' : '🔍 Verificar pago'}
                </button>

                <button className={styles.secondaryBtn} onClick={closeModal}>
                  Cerrar
                </button>
              </div>

              {verifyMsg && (
                <div
                  className={`${styles.resultBox} ${verifyMsg.ok ? styles.ok : styles.fail}`}
                  role="status"
                >
                  {verifyMsg.text}
                  {verifyMsg.ok && (
                    <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                      <button
                        className={styles.primaryBtn}
                        onClick={closeModal}
                        style={{ fontSize: '0.9rem' }}
                      >
                        Ir a Cuentas activas
                      </button>
                    </div>
                  )}
                </div>
              )}

              {verifyMsg && !verifyMsg.ok && (
                <div className={styles.hintBox}>
                  <strong>¿Salió como "no realizado"?</strong>
                  <ul>
                    <li>Revisa que la transferencia sea el <strong>valor exacto</strong> de la compra.</li>
                    <li>Verifica que el <strong>nombre</strong> coincida con el <strong>titular de la cuenta bancaria</strong>{modalType === 'binance' ? ' o usuario de Binance' : ''}.</li>
                    <li>Si acabas de pagar, espera 1 minuto y vuelve a intentar.</li>
                    <li>Si el problema persiste contáctanos al <strong>WhatsApp</strong>: +57 320 7499845 o +57 316 0789935</li>
                  </ul>
                </div>
              )}

              <div className={styles.instructions}>
                <ol>
                  <div className={styles.example}>
                    <strong>POLÍTICAS DE USO</strong>
                    <br />Cada perfil esta autorizado para utilizarse únicamente en un dispositivo a la vez.
                    <br /><strong>IMPORTANTE: Esta prohibido la modificación de nombre de perfil, PIN o cualquier otra configuracion de la cuenta entregada</strong> 
                    <br />Detectar el uso simultáneo de multiples dispositivos con un mismo perfil constituye una violación directa de nuestras normas.
                    <br />En caso de reicidencia, se procederá a la suspensión inmediata del acceso a la cuenta sin derecho a reembolso ni garantía, ya que se considera un incumplimiento de los términos acordados.
                    <br />En plataformas como Netflix, Chat GPT y Prime Vido limitamos la vinculación a un solo dispositivo debido a políticas de dichas empresas.
                    <br />Quieres conocer más, te invitamos a leer nuestros  <button
                      type="button"
                      className="policy-link"
                      onClick={openTermsConditions}
                    >
                      términos y condiciones
                      <ExternalLink size={12} />
                    </button>
                  </div>
                  <br />
                </ol>
              </div>
            </div>
          </>
        )}

        {verifyMsg && !verifyMsg.ok && !modalOpen && (
          <div className={styles.hintBox}>
            <strong>¿Salió como "no realizado"?</strong>
            <ul>
              <li>Revisa que la transferencia sea el <strong>valor exacto</strong> de la compra.</li>
              <li>Verifica que el <strong>nombre</strong> coincida con el <strong>titular de la cuenta bancaria</strong>.</li>
              <li>Si acabas de pagar, espera 1 minuto y vuelve a intentar.</li>
              <li>Si el problema persiste contáctanos al <strong>WhatsApp</strong>: +57 320 7499845 o +57 316 0789935</li>
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default Renewal;